import { ValidationErrors } from "final-form";
import { Form } from "react-final-form";
import { createListCollection } from "@ark-ui/react";
import { ReactNode } from "react";

import { useTranslation } from "@/lib/i18n";
import { FormValues, WidgetTypes } from "@/types/widget";
import { TextField } from "@/features/shared/components";
import Select from "@/components/select/select";
import DialogFooter from "@/components/dialog/dialog-footer";
import Button from "@/components/button/button";
import Stack from "@/components/stack/stack";
import Dialog from "@/components/dialog/dialog";
import dialogVariants from "@/components/dialog/dialog-variants.module.css";
import DialogHeader from "@/components/dialog/dailog-header";

type BasicWidgetDialogShellProps = {
  type: WidgetTypes;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onReset: () => void;
  initialValues: Partial<FormValues>;
  onSubmit: (values: FormValues) => void | Promise<void>;
  children: (ctx: { values: FormValues; errors: ValidationErrors }) => ReactNode;
};

export function BasicWidgetDialogShell({
  type,
  open,
  onOpenChange,
  onReset,
  initialValues,
  onSubmit,
  children,
}: BasicWidgetDialogShellProps) {
  const { t } = useTranslation();
  const typesCollection = createListCollection({
    items: Object.entries(WidgetTypes).map(([enumKey, value]) => {
      const key = enumKey.toLowerCase() as WidgetTypes;
      return { label: t(`admin.widgets.types.${key}`), value };
    }),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange} onClose={onReset}>
      <Dialog.Content className={dialogVariants.default}>
        <DialogHeader.Root>
          <DialogHeader.Title>{t(`admin.widgets.types.${type}`)}</DialogHeader.Title>
        </DialogHeader.Root>
        <Form<FormValues>
          onSubmit={onSubmit}
          initialValues={initialValues}
          destroyOnUnregister={true}
          render={({ handleSubmit, submitting, values, errors }) => (
            <>
              <form onSubmit={handleSubmit} noValidate>
                <Select
                  label={t("admin.widgets.fields.widget_type.label")}
                  collection={typesCollection}
                  value={type}
                  name="configuration.type"
                  disabled
                  placeholder=""
                />

                <TextField
                  autoFocus
                  name="title"
                  label={t("admin.widgets.fields.name.label")}
                  required
                />

                {children({ values, errors: errors ?? {} })}
              </form>
              <DialogFooter>
                <Stack justifyContent="flex-end" gap={8}>
                  <Button key="cancel" variant="ghost" onClick={() => onReset()}>
                    {t("general.cancel")}
                  </Button>
                  <Button
                    key="save"
                    variant="card"
                    onClick={handleSubmit}
                    loading={submitting}
                  >
                    {t("general.save")}
                  </Button>
                </Stack>
              </DialogFooter>
            </>
          )}
        />
      </Dialog.Content>
    </Dialog>
  );
}
